import { customElement, property, state } from 'lit/decorators.js';
import { CSSResult, html, LitElement, nothing, TemplateResult, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import truck from '@breuni-ui/design-tokens/images/icons/o-ic-20_truck.svg?element';
import boxCost from '@breuni-ui/design-tokens/images/icons/o-ic-20_box-cost.svg?element';
import boxArrowLeft from '@breuni-ui/design-tokens/images/icons/o-ic-20_box-arrow-left.svg?element';
import infoIcon from '@breuni-ui/design-tokens/images/icons/o-ic-16_info-round.svg?element';
import { addPresentationToIcon } from '../icon/icon-accessibility.js';
import { sharedCss } from '../../scripts/style/shared.js';
import { trackInfoboxViewed } from '../tracking/trackingLayer.js';
import styles from './infobox.scss?inline';

type InfoboxBlockKey = 'delivery' | 'shipping' | 'returns';

const DEFAULT_BLOCK_ORDER: InfoboxBlockKey[] = ['delivery', 'shipping', 'returns'];

const BLOCK_ORDER_BY_VARIANT: Record<string, InfoboxBlockKey[]> = {
  variante1: ['returns', 'delivery', 'shipping'],
  variante2: ['shipping', 'returns', 'delivery'],
  variante3: ['delivery', 'shipping', 'returns'],
};

const openPartnerInfo = () => {
  document.dispatchEvent(new CustomEvent('ents:partner-info:open'));
};

@customElement('ents-infobox')
export class Infobox extends LitElement {
  @property({ type: String }) variant = '';
  @property({ type: Boolean, attribute: 'is-standalone-delivery' }) isStandaloneDelivery = false;
  @property({ type: Boolean, attribute: 'is-detached' }) isDetached = false;

  @property({ type: Boolean, attribute: 'is-perso-return-costs' }) isPersoReturnCosts = false;
  @property({ type: Boolean, attribute: 'extended-return-policy' }) extendedReturnPolicy = false;
  @property({ type: Boolean, attribute: 'country-hint-enabled' }) countryHintEnabled = false;

  @property({ type: String, attribute: 'in-stock-text' }) inStockText = '';
  @property({ type: String, attribute: 'out-of-stock-text' }) outOfStockText = '';
  @property({ type: String, attribute: 'out-of-stock-online-text' }) outOfStockOnlineText = '';
  @property({ type: String, attribute: 'shipping-costs-title' }) shippingCostsTitle = '';
  @property({ type: String, attribute: 'shipping-costs-free-text' }) shippingCostsFreeText = '';
  @property({ type: String, attribute: 'shipping-costs-partner-prefix' }) shippingCostsPartnerPrefix = '';
  @property({ type: String, attribute: 'shipping-costs-partner-link-text' }) shippingCostsPartnerLinkText = '';
  @property({ type: String, attribute: 'shipping-costs-partner-breuninger-text' })
  shippingCostsPartnerBreuningerText = '';
  @property({ type: String, attribute: 'partner-info-aria-label' }) partnerInfoAriaLabel = '';
  @property({ type: String, attribute: 'shipping-costs-discover-free-shipping-text' })
  shippingCostsDiscoverFreeShippingText = '';
  @property({ type: String, attribute: 'tooltip-aria-label' }) tooltipAriaLabel = '';
  @property({ type: String, attribute: 'return-time-headline' }) returnTimeHeadline = '';
  @property({ type: String, attribute: 'return-time-headline-perso' }) returnTimeHeadlinePerso = '';
  @property({ type: String, attribute: 'return-time-oversized' }) returnTimeOversized = '';
  @property({ type: String, attribute: 'extended-return-time-text' }) extendedReturnTimeText = '';
  @property({ type: String, attribute: 'country-hint-text' }) countryHintText = '';

  @property({ type: String, attribute: 'free-shipping-tooltip-headline' }) freeShippingTooltipHeadline = '';
  @property({ type: String, attribute: 'free-shipping-condition-threshold' }) freeShippingConditionThreshold = '';
  @property({ type: String, attribute: 'free-shipping-condition-card' }) freeShippingConditionCard = '';
  @property({ type: String, attribute: 'free-shipping-condition-beyond' }) freeShippingConditionBeyond = '';
  @property({ type: String, attribute: 'free-shipping-condition-click-collect' })
  freeShippingConditionClickCollect = '';

  @property({ type: Boolean, attribute: 'out-of-stock' }) outOfStock = false;
  @property({ type: Boolean, attribute: 'is-free-shipping' }) isFreeShipping = false;
  @property({ type: String, attribute: 'shipping-forecast-message-short' }) shippingForecastMessageShort = '';
  @property({ type: String, attribute: 'stock-low-message' }) stockLowMessage = '';
  @property({ type: String, attribute: 'shipping-cost-amount' }) shippingCostAmount = '';
  @property({ type: Boolean, attribute: 'is-partner-article' }) isPartnerArticle = false;
  @property({ type: Boolean, attribute: 'is-shipped-by-breuninger' }) isShippedByBreuninger = false;
  @property({ type: Boolean, attribute: 'is-oversized-shipping' }) isOversizedShipping = false;
  @property({ type: Boolean, attribute: 'is-online-reservation-link-visible' }) isOnlineReservationLinkVisible = false;
  @property({ type: Boolean, attribute: 'is-chanel-brand' }) isChanelBrand = false;

  @state() private hasReserveContent = false;

  private viewObserver: IntersectionObserver | null = null;
  private viewedTracked = false;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  private get freeShippingTooltipContent(): string {
    return [
      this.freeShippingTooltipHeadline,
      this.freeShippingConditionThreshold,
      this.freeShippingConditionCard,
      this.freeShippingConditionBeyond,
      this.freeShippingConditionClickCollect,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  firstUpdated() {
    if (this.isStandaloneDelivery) {
      return;
    }
    this.observeViewport();
    const slot = this.shadowRoot?.querySelector('slot[name="click-and-reserve"]') as HTMLSlotElement | null;
    this.hasReserveContent = (slot?.assignedNodes({ flatten: true }).length ?? 0) > 0;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.viewObserver?.disconnect();
  }

  private observeViewport(): void {
    this.viewObserver = new IntersectionObserver(
      (entries) => {
        if (!this.viewedTracked && entries.some((entry) => entry.isIntersecting)) {
          this.viewedTracked = true;
          trackInfoboxViewed();
          this.viewObserver?.disconnect();
        }
      },
      { threshold: 0 },
    );
    this.viewObserver.observe(this);
  }

  private readonly handleReserveSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement;
    this.hasReserveContent = slot.assignedNodes({ flatten: true }).length > 0;
  };

  protected render(): TemplateResult {
    if (this.isStandaloneDelivery) {
      const attachedClass = this.isDetached ? '' : ' ents-infobox--attached';
      return html` <div class="ents-infobox${attachedClass}">${this.renderDeliveryBlock()}</div> `;
    }
    const variantOrder = BLOCK_ORDER_BY_VARIANT[this.variant] ?? DEFAULT_BLOCK_ORDER;
    const order = this.outOfStock ? variantOrder.filter((key) => key !== 'delivery') : variantOrder;
    return html`
      <div class="ents-infobox" data-test-ents-infobox-box>
        ${order.map((key) => this.renderBlock(key))}
        <div class="ents-infobox__block ents-infobox__block--reserve" ?hidden=${!this.hasReserveContent}>
          <slot name="click-and-reserve" @slotchange=${this.handleReserveSlotChange}></slot>
        </div>
        ${this.countryHintEnabled && this.countryHintText ? this.renderCountryHint() : nothing}
      </div>
    `;
  }

  private renderBlock(key: InfoboxBlockKey) {
    switch (key) {
      case 'delivery':
        return this.renderDeliveryBlock();
      case 'shipping':
        return this.renderShippingBlock();
      case 'returns':
        return this.renderReturnsBlock();
      default:
        return nothing;
    }
  }

  private renderDeliveryBlock() {
    if (this.outOfStock) {
      const outOfStockText = this.isOnlineReservationLinkVisible ? this.outOfStockOnlineText : this.outOfStockText;
      const errorClass = this.isChanelBrand ? '' : 'ents-infobox__block-title--error';
      return html`
        <div class="ents-infobox__block" data-test-ents-infobox-delivery>
          <span class="ents-infobox__block-icon">${addPresentationToIcon(truck())}</span>
          <p class="ents-infobox__block-title ${errorClass} bdk-strong-s">${outOfStockText}</p>
        </div>
      `;
    }
    return html`
      <div class="ents-infobox__block" data-test-ents-infobox-delivery>
        <span class="ents-infobox__block-icon">${addPresentationToIcon(truck())}</span>
        <div class="ents-infobox__block-text">
          <p class="ents-infobox__block-title bdk-strong-s">${this.inStockText}</p>
          ${
            this.stockLowMessage
              ? html`<p class="ents-infobox__block-subtitle bdk-text-s">${this.stockLowMessage}</p>`
              : nothing
          }
        </div>
        ${
          this.shippingForecastMessageShort
            ? html`<div class="ents-infobox__block-value bdk-strong-s">${this.shippingForecastMessageShort}</div>`
            : nothing
        }
      </div>
    `;
  }

  private renderShippingBlock() {
    return html`
      <div class="ents-infobox__block" data-test-ents-infobox-shipping>
        <span class="ents-infobox__block-icon">
          ${addPresentationToIcon(this.isOversizedShipping ? truck() : boxCost())}
        </span>
        <div class="ents-infobox__block-text">
          <p class="ents-infobox__block-title bdk-strong-s">${this.shippingCostsTitle}</p>
          ${this.renderShippingSubtitle()}
        </div>
        <div class="ents-infobox__block-value bdk-strong-s">
          ${this.isFreeShipping ? this.shippingCostsFreeText : this.shippingCostAmount}
        </div>
      </div>
    `;
  }

  private renderShippingSubtitle() {
    if (this.isPartnerArticle) {
      const linkText = this.isShippedByBreuninger
        ? this.shippingCostsPartnerBreuningerText
        : this.shippingCostsPartnerLinkText;
      return html`
        <p class="ents-infobox__block-subtitle bdk-text-s">
          ${this.isShippedByBreuninger ? nothing : html`${this.shippingCostsPartnerPrefix} `}
          <button
            class="ents-infobox__partner-link bdk-link-inline"
            aria-haspopup="dialog"
            aria-expanded="false"
            aria-label=${this.partnerInfoAriaLabel}
            @click=${openPartnerInfo}
          >
            ${linkText}
          </button>
        </p>
      `;
    }
    if (this.isFreeShipping) {
      return nothing;
    }
    if (!this.freeShippingTooltipContent) {
      return nothing;
    }
    return html`
      <p class="ents-infobox__block-subtitle bdk-text-s">
        <span
          id="ents-infobox-shipping-tooltip-trigger"
          class="ents-infobox__tooltip-trigger"
          tabindex="0"
          aria-label=${this.tooltipAriaLabel}
        >
          ${this.shippingCostsDiscoverFreeShippingText} ${addPresentationToIcon(infoIcon())}
        </span>
        <ents-tooltip
          anchor-id="ents-infobox-shipping-tooltip-trigger"
          content=${this.freeShippingTooltipContent}
          position="below"
          delay="0"
        ></ents-tooltip>
      </p>
    `;
  }

  private renderReturnsBlock() {
    if (this.extendedReturnPolicy) {
      return html`
        <div class="ents-infobox__block" data-test-ents-infobox-returns>
          <span class="ents-infobox__block-icon">${addPresentationToIcon(boxArrowLeft())}</span>
          <p class="ents-infobox__block-title bdk-strong-s">${unsafeHTML(this.extendedReturnTimeText)}</p>
        </div>
      `;
    }
    if (this.isOversizedShipping) {
      return html`
        <div class="ents-infobox__block" data-test-ents-infobox-returns>
          <span class="ents-infobox__block-icon">${addPresentationToIcon(boxArrowLeft())}</span>
          <p class="ents-infobox__block-title bdk-strong-s">${this.returnTimeOversized}</p>
        </div>
      `;
    }
    const headline = this.isPersoReturnCosts ? this.returnTimeHeadlinePerso : this.returnTimeHeadline;
    return html`
      <div class="ents-infobox__block" data-test-ents-infobox-returns>
        <span class="ents-infobox__block-icon">${addPresentationToIcon(boxArrowLeft())}</span>
        <p class="ents-infobox__block-title bdk-strong-s">${headline}</p>
      </div>
    `;
  }

  private renderCountryHint() {
    return html`
      <div class="ents-infobox__country-hint" data-test-ents-infobox-country-hint>
        <p class="bdk-text-s">${this.countryHintText}</p>
      </div>
    `;
  }
}
